<template>
  <div class="popup popup--md active">
    <div class="popup__wrap">
      <div class="popup__box">
        <div class="popup__head">
          <div class="popup__title">
            <!-- 기관관리 -> 평가원 관리 화면에서 평가 배분하기 버튼 클릭했을때 나오는 팝업 -->
            <p>{{selectedOrgName}} 평가권한 설정</p>
          </div>
          <button type="button" class="popup__close icon-only icon-x" @click="popupClose"></button>
        </div>
        <div class="popup__body">

           <!-- 로딩 화면 -->
          <div v-if="isLoading" class="loading-overlay">
            <div class="spinner"></div>
            <p>수정중입니다...</p>
          </div>

          <div class="standards-tree">
            <!-- 도메인 렌더링 -->
            <div
              v-for="domain in transformedDomains"
              :key="domain.domainCode"
              class="domain"
              :class="{ 'selected': isDomainFullySelected(domain) }"
            >
              <div class="domain-title">
                <!-- 도메인 제목 클릭 시 전체 선택/해제 -->
                <span @click.stop="toggleDomainExpand(domain.domainCode)" class="domain-toggle">
                  {{ expandedDomains.includes(domain.domainCode) ? '▲' : '▼' }}
                </span>
                
                <span @click.stop="toggleDomainExpand(domain.domainCode)" class="domain-title-text">
                  {{ domain.domainCode }}. {{ domain.domainName }}
                </span>

                <!-- 도메인 콤보박스 -->
                <select
                  v-model="domainSelections[domain.domainCode]"
                  class="combo-box"
                  @change="handleDomainSelectionChange(domain)"
                >
                  <option value="">선택</option>
                  <option 
                    v-for="user in userOptions" 
                    :key="user.value" 
                    :value="user.value"
                  >
                    {{ user.label }}
                  </option>
                </select>

                <!-- 펼침/닫힘 버튼 -->
              </div>

              <!-- 스탠다드 목록 -->
              <ul v-show="expandedDomains.includes(domain.domainCode)" class="standard-list">
                <li
                  v-for="standard in domain.standards"
                  :key="standard.standardCode"
                  class="standard-item"
                  :class="{ 'mapped': standardSelections[standard.standardCode] }"
                >
                  {{ standard.standardCode }} - {{ standard.standardName }}
                  <select
                    v-model="standardSelections[standard.standardCode]"
                    class="combo-box"
                    @change="handleStandardSelectionChange(standard.standardCode, domain)"
                  >
                    <option value="">선택</option>
                    <option 
                      v-for="user in userOptions" 
                      :key="user.value" 
                      :value="user.value"
                    >
                      {{ user.label }}
                    </option>
                  </select>
                </li>
              </ul>
            </div>
          </div>



        </div>
        <div class="popup__btns">
          <button type="button" class="btn btn--sm btn--basic icon-check" @click="popupClose">닫기</button>
          <button
            type="button"
            class="btn btn--sm btn--primary icon-check"
            @click="registerStandards"
           
          >
            등록
          </button>
        </div>

      </div>
      <div class="popup__bg"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted,onUnmounted } from "vue";
import { useStore } from "vuex";
import { ApprovedStandardList,
ApprovedStandardUpdate,
checkUpdateApprovedStandardBeFor,
updateApprovedStandardBeFor,getOrgInUser,updateAppStanList } from '@/api/user'

const store = useStore();
// 선택된 항목의 standardCode 배열
const selectedStandards = ref([]);

// 확장된 도메인 (도메인 클릭 시 열림/닫힘)
const expandedDomains = ref([]);

// 콤보박스 상태
const domainSelections = ref({});
const standardSelections = ref({});
const isLoading = ref(false) // 로딩 상태 변수 추가
// 스토어에서 데이터 매핑
const standardsList = computed(() => store.state.grid.standardData || []);

//부모한테서 데이터 가져옴
const selectedOrgName = computed(() => store.state.member.orgName  || [] )
const selectedOrgCode = computed(() => store.state.member.orgCode  || [] )

const userOptions = ref([]); // 콤보박스 리스트

const memberStandardMap = ref({}); // 저장 수정할 리스트 담는용

const transformedDomains = computed(() => transformToTreeStructure(standardsList.value));

// 도메인 펼침/닫힘 토글
const toggleDomainExpand = (domainCode) => {
  if (expandedDomains.value.includes(domainCode)) {
    expandedDomains.value = expandedDomains.value.filter((code) => code !== domainCode);
  } else {
    expandedDomains.value.push(domainCode);
  }
};

// 모든 스탠다드가 선택되었는지 확인
const isDomainFullySelected = (domain) => {
  return domain.standards.every(
    (standard) => standardSelections.value[standard.standardCode]
  );
};

// 모든 스탠다드 콤보박스 값이 동일한지 확인
const isDomainMappedUniformly = (domain) => {
  if (domain.standards.length === 0) return false;

  const firstValue = standardSelections.value[domain.standards[0].standardCode];
  if (!firstValue) return false;

  return domain.standards.every(
    (standard) => standardSelections.value[standard.standardCode] === firstValue
  );
};

/**
 * 도메인 클릭 시
 * - 도메인 열기/닫기
 * - 모든 스탠다드 선택/해제
 */
 const toggleDomain = (domain) => {
  // 도메인 열기/닫기 토글
  if (expandedDomains.value.includes(domain.domainCode)) {
    expandedDomains.value = expandedDomains.value.filter((code) => code !== domain.domainCode);
  } else {
    expandedDomains.value.push(domain.domainCode);
  }

  // 도메인 내 모든 스탠다드 선택/해제
  const allStandards = domain.standards.map((standard) => standard.standardCode);
  const isAllSelected = allStandards.every((code) => selectedStandards.value.includes(code));

  if (isAllSelected) {
    // 전체 선택된 경우, 모두 해제
    selectedStandards.value = selectedStandards.value.filter((code) => !allStandards.includes(code));
  } else {
    // 일부 또는 모두 선택되지 않은 경우, 모두 선택
    allStandards.forEach((code) => {
      if (!selectedStandards.value.includes(code)) {
        selectedStandards.value.push(code);
      }
    });
  }
};

// 스탠다드 선택/해제
const toggleSelection = (standardCode) => {
  if (selectedStandards.value.includes(standardCode)) {
    selectedStandards.value = selectedStandards.value.filter((code) => code !== standardCode);
  } else {
    selectedStandards.value.push(standardCode);
  }
};

// 스탠다드 콤보박스 변경 시 호출
const handleStandardSelectionChange = (standardCode, domain) => {
  checkDomainMapping(domain);
};


// 도메인 콤보박스 변경 시 호출
const handleDomainSelectionChange = (domain) => {
  const selectedValue = domainSelections.value[domain.domainCode];
  domain.standards.forEach((standard) => {
    standardSelections.value[standard.standardCode] = selectedValue;
  });
  checkDomainMapping(domain);
};

// 도메인 상태 동기화
const checkDomainMapping = (domain) => {
  if (isDomainMappedUniformly(domain)) {
    // 모든 스탠다드가 동일한 값이면 도메인 콤보박스에 매핑
    domainSelections.value[domain.domainCode] =
      standardSelections.value[domain.standards[0].standardCode];
  } else {
    // 값이 다르면 도메인 콤보박스 초기화
    domainSelections.value[domain.domainCode] = "";
  }
};

// 초기 매핑 시 스탠다드 값 동기화
const initializeDomainMapping = () => {
  transformedDomains.value.forEach((domain) => {
    checkDomainMapping(domain);
  });
};

// 도메인 선택/해제
const toggleDomainSelection = (domain) => {
  const allStandards = domain.standards.map(
    (standard) => standard.standardCode
  );
  const isAllSelected = allStandards.every(
    (code) => standardSelections.value[code]
  );

  if (isAllSelected) {
    allStandards.forEach((code) => {
      standardSelections.value[code] = "";
    });
  } else {
    allStandards.forEach((code) => {
      if (!standardSelections.value[code]) {
        standardSelections.value[code] = userOptions.value[0]?.value || "";
      }
    });
  }

  checkDomainMapping(domain);
};

const transformToTreeStructure = (data) => {
  const groupedData = {};

  data.forEach((item) => {
    // 도메인 코드와 이름이 없으면 무시
    if (!item.domainCode || !item.domainName) {
      console.warn(`⚠️ 유효하지 않은 데이터 발견:`, item);
      return;
    }

    // 도메인 그룹화
    if (!groupedData[item.domainCode]) {
      groupedData[item.domainCode] = {
        domainCode: item.domainCode,
        domainName: item.domainName,
        standards: [],
      };
    }

    // 스탠다드 추가
    groupedData[item.domainCode].standards.push({
      standardCode: item.standardCode || 'N/A',
      standardName: item.standardName || 'Unnamed Standard',
    });
  });

  // 객체를 배열로 변환
  return Object.values(groupedData);
};

// 저장할때 memberCode를 memberName으로 변경
const getUserNameByCode = (code) => {
  const user = userOptions.value.find(u => u.value === code);
  return user ? user.label : code;
};

// 등록 버튼 클릭 시 호출
const registerStandards = async () => {  
  const memberStandardMap = {};

  // 🛠️ **1️⃣ 모든 사용자 초기화 (빈 배열로 초기화)**
  userOptions.value.forEach(user => {
    memberStandardMap[user.value] = []; // 모든 사용자에 대해 빈 배열로 초기화
  });

  // 🛠️ **2️⃣ 콤보박스 검증 (모든 도메인과 스탠다드가 선택되었는지 확인)**
  const hasEmptySelections = Object.values(standardSelections.value).includes("");
 
  if (hasEmptySelections) {
    alert("활성화가 되지 않은 항목이 있습니다. 확인해주세요");
    return; // 🚨 실행 중단
  }

  // 🛠️ **2️⃣ 스탠다드 매핑**
  for (const [standardCode, memberCode] of Object.entries(standardSelections.value)) {
    if (memberCode) {
      if (!memberStandardMap[memberCode]) {
        memberStandardMap[memberCode] = [];
      }
      if (!memberStandardMap[memberCode].includes(standardCode)) {
        memberStandardMap[memberCode].push(standardCode);
      }
    }
  }

  // 🛠️ **3️⃣ 도메인 매핑**
  for (const [domainCode, memberCode] of Object.entries(domainSelections.value)) {
    if (memberCode) {
      const domainStandards = transformedDomains.value.find(d => d.domainCode === domainCode)?.standards || [];
      domainStandards.forEach(standard => {
        if (!memberStandardMap[memberCode]) {
          memberStandardMap[memberCode] = [];
        }
        if (!memberStandardMap[memberCode].includes(standard.standardCode)) {
          memberStandardMap[memberCode].push(standard.standardCode);
        }
      });
    }
  }
  
  console.log('📊 최종 Member-Standard 매핑 결과:', memberStandardMap);

  // 🛡️ **4️⃣ 유효성 검사**
  if (!memberStandardMap || Object.keys(memberStandardMap).length === 0) {
    console.warn('⚠️ memberStandardMap가 비어있거나 올바르지 않습니다.', memberStandardMap);
    alert('⚠️ 선택된 항목이 없거나 올바르지 않습니다.');
    return;
  }
  const param = {
    memberStandardMap,
    organizationCode : selectedOrgCode.value
  };

  try {
    let rtn = await updateAppStanList(param);

    if (rtn.result === 'confirm') {
      // 🛑 제거 항목 메시지
      let removeMsg = rtn.toRemoveDetails
        .map(item => `${getUserNameByCode(item.memberCode)} - ${item.standardCode}`)
        .join('\n');
      
      // ✅ 추가 항목 메시지
      let addMsg = rtn.toAddDetails
        .map(item => `${getUserNameByCode(item.memberCode)} - ${item.standardCode}`)
        .join('\n');

      //let confirmMessage = `⚠️ 다음 항목이 삭제 또는 덮어쓰여집니다:\n\n🛑 제거 항목:\n${removeMsg}\n\n✅ 추가 항목:\n${addMsg}\n\n계속 진행하시겠습니까?`;
      let confirmMessage = `선택한 평가위원에게 선택한 항목의 평가권한을 부여합니다. 
평가위원을 변경할 경우, 
이전 평가위원이 작성한 평가사항은 삭제됩니다. 
계속 진행하시겠습니까?`;
      // 사용자 확인 단계
      if (confirm(confirmMessage)) {
        console.log('✅ 사용자 확인 완료: 업데이트 강제 진행');
        
        isLoading.value = true; // 로딩 시작

        try{
          let finalRtn = await updateAppStanList({ memberStandardMap, 
            forceUpdate: true,
            organizationCode : selectedOrgCode.value
          });

          if (finalRtn.result === 'ok') {
            alert('✅ 평가 권한이 성공적으로 등록되었습니다.');
            store.commit("temp/setIsOrgApprovedStandard");
            location.reload(); // 페이지 새로고침
          } else {
            console.warn('⚠️ 업데이트 실패:', finalRtn.resultMsg);
            alert(`❌ 오류: ${finalRtn.resultMsg}`);
          }
        } catch (error) {
            console.error("오류 발생:", error);
        } finally {
            isLoading.value = false; // 로딩 종료
        }
        
      } else {
        console.warn('🚫 사용자 취소: 업데이트 중단');
        alert('🚫 작업이 취소되었습니다.');
      }
    } else if (rtn.result === 'ok') {
      alert('✅ 평가 권한이 성공적으로 등록되었습니다.');
      store.commit("temp/setIsOrgApprovedStandard");
      location.reload(); // 페이지 새로고침
    } else {
      console.warn('⚠️ 업데이트 실패:', rtn.resultMsg);
      alert(`❌ 오류: ${rtn.resultMsg}`);
    }
  } catch (error) {
    console.error('❌ API 호출 중 오류 발생:', error);
    alert('❌ 서버 오류가 발생했습니다.');
  }
};



// 데이터 초기화 (API 호출로 대체 가능)
const getGridData = async () => {
  await store.dispatch("grid/fetchStandard");
};

const getUserData = async () => {
  let param = {
    organizationCode: selectedOrgCode.value,
  };

  try {
    let rtn = await getOrgInUser(param);

    // ✅ 1. `rtn`이 `undefined` 또는 `null`이면 바로 종료
    if (!rtn) {
      store.commit("temp/setIsOrgApprovedStandard");
      return;
    }

    // ✅ 2. `rtn.result`이 존재하는지 확인
    if (rtn.result === "ok") {
      // API 반환 데이터를 콤보박스 옵션으로 변환
      userOptions.value = rtn.data?.map(user => ({
        value: user.memberCode,
        label: user.memberName,
      })) || []; // 만약 `rtn.data`가 `undefined`이면 빈 배열로 처리

      // ✅ 3. approvedStandard 값을 매핑하여 스탠다드 콤보박스에 설정
      rtn.data?.forEach(user => {
        if (user.approvedStandard) {
          try {
            const approvedStandards = JSON.parse(user.approvedStandard);
            approvedStandards.forEach(code => {
              if (!standardSelections.value[code]) {
                // 해당 스탠다드에 memberCode 할당
                standardSelections.value[code] = user.memberCode;
              }
            });
          } catch (error) {
            console.warn(`⚠️ approvedStandard 파싱 오류: ${error.message}`);
          }
        }
      });

      console.log("콤보박스 사용자 옵션:", userOptions.value);
      console.log("스탠다드 매핑 상태:", standardSelections.value);
    } else {
      console.warn('⚠️ 사용자 데이터를 불러오지 못했습니다.', rtn.resultMsg);
    }
  } catch (error) {
    console.error('🚨 getUserData 실행 중 오류 발생:', error);
  }
};


onMounted(async() => {
	console.log("부모창에서 기관이름 : " + selectedOrgName.value)
  console.log("부모창에서 받은 팝업 데이터2 : " + selectedOrgCode.value)
  
	await getGridData(); // 도메인 항목과 스탠다드 항목을 전부 가져옴
  // ✅ `selectedOrgCode.value`가 존재할 때만 `getUserData()` 실행
  if (selectedOrgCode.value) {
    await getUserData();
  } else {
    console.warn("⚠️ selectedOrgCode가 존재하지 않아 getUserData 실행을 건너뜁니다.");
  }

  initializeDomainMapping(); // 초기 도메인 매핑 확인

});

onUnmounted(async() => {
  //location.reload(); // 페이지 새로고침
});

const popupClose = () => {
  store.commit("temp/setIsOrgApprovedStandard");
};
</script>

<style scoped>

/* ========================= */
/* ✅ 공통 콤보박스 스타일 */
/* ========================= */
.combo-box {
  margin-left: 10px; /* 왼쪽 여백 */
  padding: 2px 4px; /* 내부 여백 */
  font-size: 12.6px; /* 글자 크기 (10% 축소) */
  font-weight: normal; /* 볼드체 제거 */
  height: 28px; /* 높이 */
  width: 75px; /* 가로 너비 (10% 증가) */
  border: 1px solid #ccc; /* 테두리 색상 */
  border-radius: 4px; /* 모서리 둥글게 */
  background-color: #fff; /* 배경색 */
  cursor: pointer; /* 포인터 커서 */
  appearance: none; /* 브라우저 기본 스타일 제거 */
  -webkit-appearance: none;
  -moz-appearance: none;

  /* 화살표 아이콘 추가 */
  background-image: url('data:image/svg+xml;utf8,<svg fill="%23000000" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5z"/></svg>');
  background-repeat: no-repeat; /* 화살표 반복 방지 */
  background-position: right 8px center; /* 화살표 위치 */
  background-size: 12px; /* 화살표 크기 */
}

/* ✅ 콤보박스 포커스 스타일 */
.combo-box:focus {
  border-color: #009FDE; /* 포커스 시 테두리 색상 */
  outline: none; /* 포커스 아웃라인 제거 */
  box-shadow: 0 0 5px rgba(0, 159, 222, 0.5); /* 포커스 시 그림자 */
}

/* ✅ 도메인 콤보박스 스타일 */
.domain-title .combo-box {
  margin-left: auto; /* 왼쪽 자동 여백 */
  margin-right: 5px; /* 오른쪽 여백 */
}

/* ✅ 스탠다드 콤보박스 스타일 */
.standard-item .combo-box {
  margin-left: 10px; /* 왼쪽 여백 */
  font-size: 12.6px; /* 글자 크기 */
  font-weight: normal; /* 볼드체 제거 */
  padding: 2px 4px; /* 내부 여백 */
  height: 28px; /* 높이 */
  width: 75px; /* 가로 너비 */
}

/* ========================= */
/* ✅ 도메인 스타일 */
/* ========================= */
.domain {
  border: 1px solid #ddd; /* 테두리 */
  border-radius: 5px; /* 모서리 둥글게 */
  padding: 10px; /* 내부 여백 */
  margin-bottom: 10px; /* 아래 여백 */
  background-color: #f9f9f9; /* 배경색 */
  cursor: pointer; /* 커서 포인터 */
}

/* ✅ 선택된 도메인 스타일 */
.domain.selected {
  background-color: #009FDE; /* 파란색 배경 */
  color: #fff; /* 글자색 */
}

/* ✅ 도메인 타이틀 */
.domain-title {
  font-size: 14px; /* 글자 크기 */
  font-weight: normal; /* 볼드체 제거 */
  margin-bottom: 5px; /* 아래 여백 */
  display: flex; /* 플렉스 박스 */
  justify-content: flex-start; /* 왼쪽 정렬 */
  align-items: center; /* 수직 정렬 */
  gap: 8px; /* 요소 간격 */
}

/* ✅ 도메인 제목과 화살표 간격 */
.domain-title-text {
  margin-right: 8px; /* 제목과 화살표 사이 간격 */
  flex: 1; /* 제목이 남은 공간을 차지하도록 설정 */
  text-align: left; /* 텍스트 왼쪽 정렬 */
}

/* ✅ 펼침/닫힘 버튼 스타일 */
.domain-toggle {
  font-size: 14px; /* 글자 크기 */
  cursor: pointer; /* 커서 포인터 */
}

/* ✅ 도메인 Hover 효과 */
.domain-title:hover {
  border-radius: 5px;
}

/* ========================= */
/* ✅ 스탠다드 스타일 */
/* ========================= */
.standard-list {
  padding-left: 24px; /* 스탠다드 항목의 들여쓰기 */
  margin-top: 5px; /* 위 여백 */
}

.standard-item {
  display: flex;
  align-items: center; /* 수직 정렬 */
  font-size: 14px;
  padding: 5px;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
  transition: background-color 0.2s;
  text-align: left;
  margin-left: -6px; /* 도메인과 같은 들여쓰기 */
}

/* ✅ 선택된 스탠다드 항목 */
.standard-item.selected {
  background-color: #009FDE; /* 활성화된 스탠다드 배경색 */
  color: #fff; /* 글자색 */
}

/* ✅ 매핑된 스탠다드 항목 */
.standard-item.mapped {
  background-color: #009FDE; /* 매핑된 스탠다드 배경색 */
  color: #fff; /* 글자색 */
}

/* ✅ 스탠다드 콤보박스 */
.standard-item .combo-box {
  margin-left: auto; /* 콤보박스를 오른쪽으로 이동 */
  font-size: 12px;
  padding: 2px 6px;
  height: 28px;
  width: 75px;
}

/* ========================= */
/* ✅ 버튼 스타일 */
/* ========================= */
.popup__btns .btn:disabled {
  background: #ddd; /* 비활성화 버튼 배경 */
  color: #aaa; /* 비활성화 버튼 글자색 */
  cursor: not-allowed; /* 비활성화 커서 */
}

/* ========================= */
/* ✅ 팝업 배경 스타일 */
/* ========================= */
.popup__bg {
  background-color: rgba(0, 0, 0, 0.3); /* 어두운 배경 */
}

.loading-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: rgba(0, 0, 0, 0.5);
		color: white;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		z-index: 1000;
	}
	
	.spinner {
		border: 5px solid rgba(255, 255, 255, 0.3);
		border-top: 5px solid white;
		border-radius: 50%;
		width: 50px;
		height: 50px;
		animation: spin 1s linear infinite;
	}
	
	@keyframes spin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}

</style>
